import React, { useRef, useState } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { sendAnalyticsEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { BREED_FILTERS } from 'dpl/homepage/utils/constants';
import FilterPills from 'dpl/common/components/FilterPills';
import SmartImage from 'dpl/components/SmartImage';
import Icon from 'dpl/common/components/Icon';
import { addQueryToUrl } from 'dpl/util/queryString';

const IS_LT_MD = isLessThanBreakpoint('md');
const BREEDER_PHOTO_SIZE = '32px';

function BreedCard({ breed }) {
  const breedSearchUrl = addQueryToUrl(
    {
      origin_view: ANALYTICS_VIEWS.HOME,
      origin_view_feature: ANALYTICS_VIEW_FEATURES.QUICK_SEARCH_BREED
    },
    `/${breed.slug}`
  );

  return (
    <a href={breedSearchUrl} className="BreedCard w-100 h-100 relative db">
      <div className="aspect-ratio aspect-ratio--5x6 relative z-0 br12 overflow-hidden">
        <div
          className={classnames('aspect-ratio--object', {
            'transition grow': !IS_LT_MD
          })}
        >
          <SmartImage
            Tag="div"
            className="bg-center cover h-100 w-100"
            src={breed.photo_url}
            loadingClass="bg-light-gray"
            crop
          />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-white br10 pv3 ph4 mh2 mv2">
        <p className="font-serif font-20 fw-medium mb2">{breed.name}</p>
        <div className="flex gap-8">
          {breed.breeder_photos.map((photo, idx) => (
            <SmartImage
              key={`${breed.slug}-${idx}`}
              className="br3"
              src={photo}
              loadingClass="bg-gray-100"
              width={BREEDER_PHOTO_SIZE}
              height={BREEDER_PHOTO_SIZE}
              crop
            />
          ))}
          <div
            className="bg-gray-100 br3 ba b--default font-12 black flex justify-center items-center"
            style={{ height: BREEDER_PHOTO_SIZE, width: BREEDER_PHOTO_SIZE }}
          >
            100+
          </div>
        </div>
      </div>
    </a>
  );
}

BreedCard.propTypes = {
  breed: PropTypes.shape({
    name: PropTypes.string,
    slug: PropTypes.string,
    photo_url: PropTypes.string,
    breeder_photos: PropTypes.arrayOf(PropTypes.string)
  }).isRequired
};

export default function BreedersByBreedSection() {
  const splideRef = useRef(null);
  const [cardsPerPage, setCardsPerPage] = useState(4);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const [selectedFilterIdx, setSelectedFilterIdx] = useState(0);
  const { breeds } = BREED_FILTERS[selectedFilterIdx];

  function handleBreedClick(idx) {
    setIsVisible(false);
    sendAnalyticsEvent(ANALYTICS_EVENTS.BUTTON_CLICKED, {
      view: ANALYTICS_VIEWS.HOME,
      view_feature: ANALYTICS_VIEW_FEATURES.QUICK_SEARCH_BREED_CATEGORY,
      button_cta: BREED_FILTERS[idx].label
    });

    setTimeout(() => {
      setSelectedFilterIdx(idx);
      if (activeIndex > 1 && splideRef.current) {
        splideRef.current.go(0);
      }
      setIsVisible(true);
    }, 300);
  }

  function handleActiveSlide(slide) {
    setActiveIndex(slide.index);
  }

  function handleResize(splide) {
    setCardsPerPage(splide.options.perPage);
  }

  const isNextButtonDisabled = activeIndex >= breeds.length - cardsPerPage;
  const isPrevButtonDisabled = activeIndex <= 0;

  return (
    <div className="container-xl section-fade">
      <div className="tc-ns">
        <h3 className="font-28 md:font-40 mb4 lh-title">
          One safe place to start
        </h3>
        <p className="secondary o-80 font-16 md:font-18">
          Explore thousands of trusted breeders, shelters, and rescues across
          300+ breeds.
        </p>
        <FilterPills
          className="BreedersByBreedFilter flex justify-center mb8 mb12-md mt8"
          items={BREED_FILTERS}
          onFilterClick={handleBreedClick}
          selectedFilterIdx={selectedFilterIdx}
        />
      </div>
      <div
        className="BreedersByBreedCarousel"
        style={{
          opacity: !isVisible && activeIndex === 0 ? 0 : 1,
          transition: 'opacity 0.4s ease-in-out'
        }}
      >
        <Splide
          ref={splideRef}
          className="overflow-hidden-ns"
          onActive={handleActiveSlide}
          onMounted={handleResize}
          onResized={handleResize}
          options={{
            arrows: false,
            drag: breeds.length > 0,
            dragMinThreshold: 20,
            easing: 'cubic-bezier(0.5, 0, 0.5, 1)',
            flickMaxPages: cardsPerPage,
            flickPower: 300,
            gap: IS_LT_MD ? 16 : 24,
            keyboard: 'focused',
            pagination: false,
            perPage: cardsPerPage,
            speed: IS_LT_MD ? 350 : 700,
            snap: true,
            start: activeIndex,
            breakpoints: {
              1200: { perPage: 4 },
              960: { perPage: 3 },
              720: { perPage: 2 },
              540: { perPage: 1 }
            }
          }}
        >
          {breeds.map((breed, idx) => (
            <SplideSlide key={idx}>
              <BreedCard breed={breed} />
            </SplideSlide>
          ))}
        </Splide>
      </div>
      <div className="mt10 mt12-md relative w-100" style={{ height: 2 }}>
        <span className="absolute bg-stone-700 db o-30 h-100 w-100" />
        <span
          className="absolute bg-stone-700 db h-100 transition"
          style={{
            width: `${(100 / breeds.length) * (activeIndex + cardsPerPage)}%`
          }}
        />
      </div>
      <div className="dn db-ns mt6 secondary">
        <button
          type="button"
          className={classnames('bg-white ba b--default br-100 mr4', {
            'o-40': isPrevButtonDisabled
          })}
          disabled={isPrevButtonDisabled}
          onClick={() => splideRef.current.go('<')}
        >
          <div className="flex items-center justify-center ph3 pv3">
            <Icon name="fetch-arrow-left-md" />
          </div>
        </button>
        <button
          type="button"
          className={classnames('bg-white ba b--default br-100', {
            'o-40': isNextButtonDisabled
          })}
          disabled={isNextButtonDisabled}
          onClick={() => splideRef.current.go('>')}
        >
          <div className="flex items-center justify-center ph3 pv3">
            <Icon name="fetch-arrow-right-md" />
          </div>
        </button>
      </div>
    </div>
  );
}
